/**
 * TransistorKit live-debug bridge for the Web surface.
 *
 * Mirrors the payload the Swift/Kotlin bridges post to TransistorKit's event
 * listener, so "in recurringDetail, make the price history chart taller"
 * resolves to the same screen id on every surface. Dev-only: in a production
 * build every method is a no-op, which is why `log()` is safe to leave in
 * shipped code.
 */
const enabled = import.meta.env.DEV

const PROJECT = 'Fintech'
const PLATFORM = 'Web'

/** Proxied by vite.config.ts to TransistorKit's listener, so no CORS dance. */
const EVENTS_URL = '/__tk/events'

function browserLabel(): string {
  if (typeof navigator === 'undefined') return 'Browser'
  const ua = navigator.userAgent
  const name = /Firefox\//.test(ua)
    ? 'Firefox'
    : /Edg\//.test(ua)
      ? 'Edge'
      : /Chrome\//.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser'
  const os = /Mac OS X/.test(ua) ? 'macOS' : /Windows/.test(ua) ? 'Windows' : /Linux/.test(ua) ? 'Linux' : ''
  return os ? `${name} on ${os}` : name
}

function deviceId(): string {
  const key = 'tk.bridge.deviceId'
  try {
    let id = localStorage.getItem(key)
    if (!id) {
      id = crypto.randomUUID()
      localStorage.setItem(key, id)
    }
    return id
  } catch {
    return 'web-anonymous'
  }
}

type Kind = 'lifecycle' | 'screen' | 'error' | 'log'

function post(kind: Kind, screen?: string, message?: string): void {
  if (!enabled) return
  const payload: Record<string, unknown> = {
    kind,
    project: PROJECT,
    platform: PLATFORM,
    session: 'web',
    deviceID: deviceId(),
    deviceName: browserLabel(),
  }
  if (screen) payload.screen = screen
  if (message) payload.message = message
  // Fire and forget. text/plain keeps it a simple request (no preflight); if
  // TransistorKit is not listening the rejection is swallowed on purpose.
  void fetch(EVENTS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {})
}

let started = false

export const bridge = {
  start(): void {
    if (!enabled || started) return
    started = true
    post('lifecycle', undefined, 'launched')
    window.addEventListener('error', (e) => {
      bridge.reportError(e.message, currentScreen)
    })
    window.addEventListener('unhandledrejection', (e) => {
      bridge.reportError(String((e as PromiseRejectionEvent).reason), currentScreen)
    })
  },

  /**
   * Report the visible screen. Called again when a screen changes to a state
   * that changes what is rendered (dashboard -> analyzing, upload -> parsing,
   * importReview -> locked), per the spec.
   */
  reportScreen(screen: string, state?: string): void {
    currentScreen = screen
    post('screen', screen, state)
    if (enabled) console.debug(`[screen] ${screen}${state ? ` (${state})` : ''}`)
  },

  reportError(message: string, screen?: string): void {
    post('error', screen ?? currentScreen, message)
    if (enabled) console.error(`[error] ${screen ?? currentScreen ?? '-'}: ${message}`)
  },

  /** Use instead of console.log for anything worth reading back later. */
  log(message: string, screen?: string): void {
    post('log', screen ?? currentScreen, message)
    if (enabled) console.log(`[log] ${message}`)
  },
}

let currentScreen: string | undefined
